import React, { useCallback, useRef, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  cancelSale,
  getSaleById,
  toSaleApiError,
  type Sale,
  type SaleHistoryScope,
} from '@/api/saleApi';
import { tokenHasPermission } from '@/features/auth/utils/jwtPermissions';
import { storage } from '@/utils/storage';
import CancelSaleDialog from '../components/CancelSaleDialog';
import SaleDetailDrawer from '../components/SaleDetailDrawer';
import SaleHistoryTable from '../components/SaleHistoryTable';
import { SALE_HISTORY_PAGE_SIZES, useSaleHistory } from '../hooks/useSaleHistory';

const SaleHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const token = storage.getAccessToken();
  const scope: SaleHistoryScope = tokenHasPermission(token, 'SALE_READ_ALL') ? 'all' : 'own';
  const canCancel = tokenHasPermission(token, 'SALE_CANCEL');
  const {
    query,
    pageData,
    isInitialLoading,
    isTableLoading,
    loadError,
    setPage,
    setPageSize,
    refresh,
    retry,
  } = useSaleHistory(scope);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [cancelTarget, setCancelTarget] = useState<Sale | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [pendingSaleId, setPendingSaleId] = useState<number | null>(null);
  const detailRequestId = useRef(0);

  const loadDetail = useCallback(async (sale: Sale) => {
    const activeRequest = ++detailRequestId.current;
    setSelectedSale(sale);
    setIsDetailOpen(true);
    setIsDetailLoading(true);
    setDetailError('');
    setPendingSaleId(sale.id);
    try {
      const detail = await getSaleById(sale.id);
      if (activeRequest === detailRequestId.current) setSelectedSale(detail);
    } catch (error: unknown) {
      if (activeRequest === detailRequestId.current) {
        setDetailError(toSaleApiError(error).message);
      }
    } finally {
      if (activeRequest === detailRequestId.current) {
        setIsDetailLoading(false);
        setPendingSaleId(null);
      }
    }
  }, []);

  const closeDetail = useCallback(() => {
    detailRequestId.current += 1;
    setIsDetailOpen(false);
    setIsDetailLoading(false);
    setDetailError('');
    setPendingSaleId(null);
  }, []);

  const requestCancel = (sale: Sale) => {
    if (!canCancel || sale.status !== 'PAID') return;
    setCancelError('');
    setCancelTarget(sale);
  };

  const confirmCancel = async () => {
    if (!cancelTarget || isCanceling) return;
    const saleId = cancelTarget.id;
    setIsCanceling(true);
    setCancelError('');
    setPendingSaleId(saleId);
    try {
      const canceledSale = await cancelSale(saleId);
      if (selectedSale?.id === saleId) setSelectedSale(canceledSale);
      setCancelTarget(null);
      message.success(t('admin.sales_history.notices.canceled'));
      await refresh();
    } catch (error: unknown) {
      const normalizedError = toSaleApiError(error);
      setCancelError(normalizedError.message);
      message.error(normalizedError.message);
    } finally {
      setIsCanceling(false);
      setPendingSaleId(null);
    }
  };

  return (
    <div className="mx-auto flex min-h-full w-full min-w-0 max-w-[1600px] flex-col gap-md sm:gap-lg">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-h1 text-h1 text-on-surface">{t('admin.sales_history.title')}</h1>
          <p className="mt-1 text-body-md text-secondary">{t('admin.sales_history.subtitle')}</p>
        </div>
        <div className="inline-flex w-fit items-center gap-2 rounded-full bg-primary-fixed px-3 py-1.5 font-label-sm text-primary">
          <span className="material-symbols-outlined text-[18px]">{scope === 'all' ? 'store' : 'person'}</span>
          {t(`admin.sales_history.scope.${scope}`)}
        </div>
      </header>

      <section className="flex min-h-[600px] min-w-0 flex-1 flex-col overflow-hidden rounded-[20px] border border-outline-variant/70 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-outline-variant px-4 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-5">
          <div>
            <h2 className="font-h2 text-h2 text-on-surface">{t('admin.sales_history.management_title')}</h2>
            <p className="mt-1 text-body-sm text-secondary">{t('admin.sales_history.total', { total: pageData.totalElements })}</p>
          </div>
          <label className="flex min-w-[145px] flex-col gap-1 font-label-sm text-secondary">
            {t('admin.sales_history.page_size.label')}
            <select className="h-10 rounded-xl border border-outline-variant bg-white px-3 font-body-md text-on-surface focus:border-primary focus:outline-none" value={query.size} disabled={isTableLoading} onChange={(event) => setPageSize(Number(event.target.value))}>
              {SALE_HISTORY_PAGE_SIZES.map((pageSize) => (
                <option key={pageSize} value={pageSize}>{t('admin.sales_history.page_size.option', { size: pageSize })}</option>
              ))}
            </select>
          </label>
        </div>

        {loadError && pageData.data.length > 0 && (
          <div className="flex items-center justify-between gap-3 border-b border-error/20 bg-error-container/35 px-4 py-2 text-body-sm text-error" role="alert">
            <span>{loadError}</span>
            <button type="button" className="shrink-0 font-label-sm text-primary hover:underline" onClick={() => void retry()}>{t('admin.sales_history.actions.retry')}</button>
          </div>
        )}

        <SaleHistoryTable
          sales={pageData.data}
          currentPage={pageData.currentPage}
          totalPages={pageData.totalPages}
          totalElements={pageData.totalElements}
          pageSize={pageData.pageSize}
          isLoading={isTableLoading}
          isInitialLoading={isInitialLoading}
          error={loadError}
          isOwnScope={scope === 'own'}
          canCancel={canCancel}
          pendingSaleId={pendingSaleId}
          onPageChange={setPage}
          onView={(sale) => void loadDetail(sale)}
          onCancel={requestCancel}
          onRetry={() => void retry()}
        />
      </section>

      <SaleDetailDrawer
        open={isDetailOpen}
        sale={selectedSale}
        isLoading={isDetailLoading}
        error={detailError}
        canCancel={canCancel}
        onClose={closeDetail}
        onRetry={() => selectedSale && void loadDetail(selectedSale)}
        onCancel={requestCancel}
      />

      <CancelSaleDialog
        sale={cancelTarget}
        isSubmitting={isCanceling}
        error={cancelError}
        onClose={() => {
          if (!isCanceling) {
            setCancelTarget(null);
            setCancelError('');
          }
        }}
        onConfirm={() => void confirmCancel()}
      />
    </div>
  );
};

export default SaleHistoryPage;
